import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { API, Auth, Storage } from 'aws-amplify';
import { AppDispatch, RootState } from '../store';
import { RequestStatusObject, buildReducers, createRequestStatusObject, createLazyList } from '../utils';
import {
	AppClient,
	CreateAppClientResponse,
	CreateOrganisationPayload,
	FetchPartnerApiLogsParams,
	Organisation,
	OrganisationPreview,
	OrganisationType,
	PartnerApiLog,
	UpdateOrganisationPayload,
	OrganisationProfile,
	StudentList,
	ReadOrgAdvert,
	ReadSingleOrgAdvert,
	OrgAdvertisement,
	OrganisationConstituent,
	PartnerConfig,
	ConstituentUploadResult,
	ConstituentImportJob,
	ConstituentImportTicket,
	CONSTITUENT_IMPORT_ACTIVE_STATUSES
} from '../../types/organisation';
import { LazyList, LazyListRequest, LazyListResponse, VerifierOnlyOrgLazyListRequest } from '../../types/types';
import { enqueueSnackbar } from 'notistack';
import i18n from '../../i18n';
import { BulkUploadDocumentMetric, BulkUploadMetric } from '../../types/bulkUploadMetrics';
import { FailedSend, SendEvent, SentDocument, SentDocumentFilters } from '../../types/orgDocumentHistory';
import { User, RoleName } from '../../types/user';
import {
	StudentImportJob,
	StudentImportRow,
	StudentImportSettings,
	StudentImportTicket
} from '../../types/studentImport';
import { ResendOutcome, StudentSearch, StudentSearchResult } from '../../types/studentDirectory';

const initialState: {
	current: Organisation | undefined;
	list: LazyList<Organisation>;
	prospectiveList: LazyList<any>;
	prospectiveIndividuals: LazyList<any>;
	portaLogs: LazyList<any>;
	detailedPortaLogs: LazyList<any>;
	billingAdminOrders: LazyList<any>;
	billingAdminGrants: LazyList<any>;
	studentList: LazyList<StudentList>;
	adverts: LazyList<ReadOrgAdvert>;
	singleAdvert: any;
	spotlightCount: number;
	organisationProfileList: any;
	candidate_interests_notifications: any;
	verification_requests_notifications: any;
	shared_wallet_requests_notification_count: any;
	metrics: any;
	bulk_upload_metrics: LazyList<BulkUploadMetric>;
	bulk_upload_document_metrics: LazyList<BulkUploadDocumentMetric>;
	/* Org document history (PTEC-425). */
	uploaded_documents: LazyList<SentDocument>;
	uploaded_documents_failed: FailedSend[];
	send_history: LazyList<SendEvent>;
	organisationActiveSubscriptions: LazyList<any>;
	previews: {
		all: OrganisationPreview[];
	} & {
		[key in OrganisationType]?: OrganisationPreview[];
	}; // Used for organisation dropdowns
	readOrganisationRequest: RequestStatusObject;
	prospectiveOrganisationRequest: RequestStatusObject;
	prospectiveIndividualRequest: RequestStatusObject;
	listPortalLogsRequest: RequestStatusObject;
	listDetailedPortalLogsRequest: RequestStatusObject;
	listOrganisationsRequest: RequestStatusObject;
	createOrganisationRequest: RequestStatusObject;
	updateOrganisationRequest: RequestStatusObject;
	deleteOrganisationRequest: RequestStatusObject;
	createOrganisationProfileRequest: RequestStatusObject;
	updateOrganisationProfileRequest: RequestStatusObject;
	readOrganisationProfileRequest: RequestStatusObject;
	fetchCandidateInterestsMetrics: RequestStatusObject;
	fetchSharedDocumentsRequestsMetrics: RequestStatusObject;
	fetchSharedDocumentsRequestsOutstandingDocumentMetrics: RequestStatusObject;
	fetchCandidateInterestsRequestOutcomeMetrics: RequestStatusObject;
	fetchCandidateInterestsLocationMetrics: RequestStatusObject;
	fetchVerificationRequestsDocumentUploadMetrics: RequestStatusObject;
	fetchVerificationRequestsMetrics: RequestStatusObject;
	fetchVerificationRequestsResponseTimeMetrics: RequestStatusObject;
	fetchVerificationRequestsAdditionalMetrics: RequestStatusObject;
	fetchCandidateInterestsCompanyMetrics: RequestStatusObject;
	listBulkUploadMetrics: RequestStatusObject;
	listBulkUploadDocumentMetrics: RequestStatusObject;
	listOrganisationUploadedDocuments: RequestStatusObject;
	listOrganisationFailedSends: RequestStatusObject;
	listOrganisationSendHistory: RequestStatusObject;
	cancelUploadedDocument: RequestStatusObject;
	replaceUploadedDocument: RequestStatusObject;
	setUploadedDocumentVerification: RequestStatusObject;
	setUploadedDocumentAccess: RequestStatusObject;
	revokeUploadedDocumentVerification: RequestStatusObject;
	retractSendBatch: RequestStatusObject;
	fetchPearsanaCandidateInterestsMetrics: RequestStatusObject;
	createOrgAdvertRequest: RequestStatusObject;
	updateOrgAdvertRequest: RequestStatusObject;
	deleteOrgAdvertRequest: RequestStatusObject;
	readOrgAdverts: RequestStatusObject;
	listCandidatInterestsNotifications: RequestStatusObject;
	listDocumentRequestNotificationCount: RequestStatusObject;
	readSingleOrgAdvert: RequestStatusObject;
	readStudentListRequest: RequestStatusObject;
	fetchSpotlightCountRequest: RequestStatusObject;
	listVerificationRequestNotifications: RequestStatusObject;
	fetchPearsanaCandidateInterestsLocationMetrics: RequestStatusObject;
	organisationActiveSubscriptionsRequest: RequestStatusObject;
	cancelActiveSubscriptionRequest: RequestStatusObject;
	billingAdminOrdersRequest: RequestStatusObject;
	billingAdminGrantsRequest: RequestStatusObject;
	updateOrganisationGrantStatus?: RequestStatusObject;
	familyAdmins: User[];
	familyChildren: User[];
	listFamilyAdminsRequest: RequestStatusObject;
	listFamilyChildrenRequest: RequestStatusObject;
	family_member_notifications: { [userId: string]: number };
	listFamilyMemberNotificationsRequest: RequestStatusObject;
	dismissFamilyMemberNotificationsRequest: RequestStatusObject;
	verifyingOrganisation: Organisation | undefined;
	readVerifyingOrganisationRequest: RequestStatusObject;
	appClients: AppClient[];
	appClientReadStatus: RequestStatusObject;
	createAppClientRequest: RequestStatusObject;
	deleteAppClientRequest: RequestStatusObject;
	constituents: OrganisationConstituent[];
	constituentsNextToken: string | null;
	/** The whole roster, not the loaded page. Null until counted. */
	constituentsTotal: number | null;
	/**
	 * What the roster state is currently meant to show. A response that does not
	 * match is discarded — otherwise a late reply from the previous organisation, or
	 * from a superseded search, repaints the table with the wrong rows.
	 */
	constituentsScope: { organisationId: string | null; search: string };
	/** A search spanning several indexes cannot be paged, so results are capped. */
	constituentsTruncated: boolean;
	constituentUpload: ConstituentUploadResult | null;
	/** The import currently being watched by the dialog, if any. */
	constituentImport: ConstituentImportJob | null;
	constituentImports: ConstituentImportJob[];
	listConstituentsRequest: RequestStatusObject;
	createConstituentImportRequest: RequestStatusObject;
	uploadConstituentFileRequest: RequestStatusObject;
	startConstituentImportRequest: RequestStatusObject;
	listConstituentImportsRequest: RequestStatusObject;
	deleteConstituentRequest: RequestStatusObject;
	purgeConstituentsRequest: RequestStatusObject;
	partnerApiLogs: { logs: PartnerApiLog[]; nextToken?: string | null };
	partnerApiLogsRequest: RequestStatusObject;
	partnerConfig: PartnerConfig | null;
	partnerConfigRequest: RequestStatusObject;
	savePartnerConfigRequest: RequestStatusObject;
	isLoading: boolean;
} = {
	current: undefined,
	list: createLazyList(),
	prospectiveList: createLazyList(),
	prospectiveIndividuals: createLazyList(),
	portaLogs: createLazyList(),
	billingAdminGrants: createLazyList(),
	billingAdminOrders: createLazyList(),
	detailedPortaLogs: createLazyList(),
	studentList: createLazyList(),
	organisationProfileList: {},
	candidate_interests_notifications: null,
	verification_requests_notifications: null,
	shared_wallet_requests_notification_count: null,
	organisationActiveSubscriptions: createLazyList(),
	isLoading: false,

	metrics: {},
	previews: {
		all: []
	},
	readOrganisationRequest: createRequestStatusObject(),
	prospectiveOrganisationRequest: createRequestStatusObject(),
	prospectiveIndividualRequest: createRequestStatusObject(),
	listPortalLogsRequest: createRequestStatusObject(),
	listDetailedPortalLogsRequest: createRequestStatusObject(),
	listOrganisationsRequest: createRequestStatusObject(),
	createOrganisationRequest: createRequestStatusObject(),
	updateOrganisationRequest: createRequestStatusObject(),
	deleteOrganisationRequest: createRequestStatusObject(),
	createOrganisationProfileRequest: createRequestStatusObject(),
	updateOrganisationProfileRequest: createRequestStatusObject(),
	readOrganisationProfileRequest: createRequestStatusObject(),
	fetchCandidateInterestsMetrics: createRequestStatusObject(),
	fetchSharedDocumentsRequestsMetrics: createRequestStatusObject(),
	fetchSharedDocumentsRequestsOutstandingDocumentMetrics: createRequestStatusObject(),
	fetchCandidateInterestsRequestOutcomeMetrics: createRequestStatusObject(),
	fetchCandidateInterestsLocationMetrics: createRequestStatusObject(),
	fetchVerificationRequestsDocumentUploadMetrics: createRequestStatusObject(),
	fetchVerificationRequestsMetrics: createRequestStatusObject(),
	fetchVerificationRequestsResponseTimeMetrics: createRequestStatusObject(),
	fetchVerificationRequestsAdditionalMetrics: createRequestStatusObject(),
	fetchCandidateInterestsCompanyMetrics: createRequestStatusObject(),
	listBulkUploadMetrics: createRequestStatusObject(),
	listBulkUploadDocumentMetrics: createRequestStatusObject(),
	listOrganisationUploadedDocuments: createRequestStatusObject(),
	listOrganisationFailedSends: createRequestStatusObject(),
	listOrganisationSendHistory: createRequestStatusObject(),
	cancelUploadedDocument: createRequestStatusObject(),
	replaceUploadedDocument: createRequestStatusObject(),
	setUploadedDocumentVerification: createRequestStatusObject(),
	setUploadedDocumentAccess: createRequestStatusObject(),
	revokeUploadedDocumentVerification: createRequestStatusObject(),
	retractSendBatch: createRequestStatusObject(),
	fetchPearsanaCandidateInterestsMetrics: createRequestStatusObject(),
	fetchPearsanaCandidateInterestsLocationMetrics: createRequestStatusObject(),
	listCandidatInterestsNotifications: createRequestStatusObject(),
	listDocumentRequestNotificationCount: createRequestStatusObject(),
	listVerificationRequestNotifications: createRequestStatusObject(),
	bulk_upload_metrics: createLazyList(),
	bulk_upload_document_metrics: createLazyList(),
	uploaded_documents: createLazyList(),
	uploaded_documents_failed: [],
	send_history: createLazyList(),
	createOrgAdvertRequest: createRequestStatusObject(),
	updateOrgAdvertRequest: createRequestStatusObject(),
	deleteOrgAdvertRequest: createRequestStatusObject(),
	readOrgAdverts: createRequestStatusObject(),
	readSingleOrgAdvert: createRequestStatusObject(),
	readStudentListRequest: createRequestStatusObject(),
	fetchSpotlightCountRequest: createRequestStatusObject(),
	organisationActiveSubscriptionsRequest: createRequestStatusObject(),
	cancelActiveSubscriptionRequest: createRequestStatusObject(),
	billingAdminOrdersRequest: createRequestStatusObject(),
	billingAdminGrantsRequest: createRequestStatusObject(),
	updateOrganisationGrantStatus: createRequestStatusObject(),
	adverts: createLazyList(),
	singleAdvert: {},
	spotlightCount: 0,
	familyAdmins: [],
	familyChildren: [],
	listFamilyAdminsRequest: createRequestStatusObject(),
	listFamilyChildrenRequest: createRequestStatusObject(),
	family_member_notifications: {},
	listFamilyMemberNotificationsRequest: createRequestStatusObject(),
	dismissFamilyMemberNotificationsRequest: createRequestStatusObject(),
	verifyingOrganisation: undefined,
	readVerifyingOrganisationRequest: createRequestStatusObject(),
	appClients: [],
	appClientReadStatus: createRequestStatusObject(),
	createAppClientRequest: createRequestStatusObject(),
	deleteAppClientRequest: createRequestStatusObject(),
	constituents: [],
	constituentsNextToken: null,
	constituentsTotal: null,
	constituentsScope: { organisationId: null, search: '' },
	constituentsTruncated: false,
	constituentUpload: null,
	constituentImport: null,
	constituentImports: [],
	listConstituentsRequest: createRequestStatusObject(),
	createConstituentImportRequest: createRequestStatusObject(),
	uploadConstituentFileRequest: createRequestStatusObject(),
	startConstituentImportRequest: createRequestStatusObject(),
	listConstituentImportsRequest: createRequestStatusObject(),
	deleteConstituentRequest: createRequestStatusObject(),
	purgeConstituentsRequest: createRequestStatusObject(),
	partnerApiLogs: { logs: [], nextToken: null },
	partnerApiLogsRequest: createRequestStatusObject(),
	partnerConfig: null,
	partnerConfigRequest: createRequestStatusObject(),
	savePartnerConfigRequest: createRequestStatusObject()
};

export const getBillingAdminOrders = createAsyncThunk<
	LazyListResponse<any>,
	{ organisationId: string; status?: string; orderId?: string; dateTo?: string; dateFrom?: string } & LazyListRequest,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/getBillingAdminOrders', async ({ organisationId, status, orderId, dateTo, dateFrom, next, limit }) => {
	const response = await API.get('inspirusapi', `/organisations/${organisationId}/orders`, {
		headers: {},
		response: true,
		queryStringParameters: { next, limit, status, orderId, dateTo, dateFrom }
	});

	return response.data;
});

export const getBillingAdminGrants = createAsyncThunk<
	LazyListResponse<any>,
	{ organisationId: string } & LazyListRequest,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/getBillingAdminGrants', async ({ organisationId }) => {
	const response = await API.get('inspirusapi', `/organisations/${organisationId}/grants`, {
		headers: {},
		response: true,
		queryStringParameters: {}
	});

	return response.data;
});

export const listProspectiveIndividuals = createAsyncThunk<
	LazyListResponse<Organisation>,
	{
		organisationalId: string;
		payload: LazyListRequest;
		search?: string;
		type?: string;
		startDate?: string;
		endDate?: string;
	},
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/listProspectiveIndividuals',

	async ({ organisationalId, payload, search = null, startDate, endDate }) => {
		const queryStringParameters: { [key: string]: any } = {
			next: payload.next,
			limit: payload.limit,
			startDate,
			endDate
		};

		if (search) {
			queryStringParameters.search = search;
		}

		const result = await API.get('inspirusapi', `/organisations/pearsana/prospective/portal/${organisationalId}`, {
			headers: {},
			response: true,
			queryStringParameters
		});

		return result.data;
	}
);

export const listPortalLogs = createAsyncThunk<
	LazyListResponse<Organisation>,
	{ type: string; payload: LazyListRequest; search?: string; startDate?: string; endDate?: string },
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/listPortalLogs',

	async ({ type, payload, search = null, startDate, endDate }) => {
		const queryStringParameters: { [key: string]: any } = {
			next: payload.next,
			limit: payload.limit,
			startDate,
			endDate
		};

		if (search) {
			queryStringParameters.search = search;
		}

		const result = await API.get('inspirusapi', `/organisations/pearsana/logs/${type}/`, {
			headers: {},
			response: true,
			queryStringParameters
		});

		return result.data;
	}
);

export const listDetailedPortalLogs = createAsyncThunk<
	LazyListResponse<Organisation>,
	{ organisationId: string; payload: LazyListRequest; search?: string; startDate?: string; endDate?: string },
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/listDetailedPortalLogs',

	async ({ organisationId, payload, search = null, startDate, endDate }) => {
		const queryStringParameters: { [key: string]: any } = {
			next: payload.next,
			limit: payload.limit,
			startDate,
			endDate
		};

		if (search) {
			queryStringParameters.search = search;
		}

		const result = await API.get('inspirusapi', `/organisations/pearsana/logs/portal/${organisationId}`, {
			headers: {},
			response: true,
			queryStringParameters
		});

		return result.data;
	}
);

export const listDocumentRequestNotificationCount = createAsyncThunk<
	any,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/metrics/shared-documents-requests-notifications', async (organisationId) => {
	const response = await API.get(
		'inspirusapi',
		`/organisations/${organisationId}/shared-documents-requests-notifications-count`,
		{
			headers: {}
		}
	);
	return response;
});
export const listCandidatInterestsNotifications = createAsyncThunk<
	any,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/metrics/candidate-interests-notifications', async (organisationId) => {
	// Use the passed organisationId directly
	const response = await API.get(
		'inspirusapi',
		`/organisations/${organisationId}/candidate-interests-notifications`,
		{
			headers: {}
		}
	);
	return response;
});

export const listVerificationRequestNotifications = createAsyncThunk<
	any,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/metrics/verification-requests-notifications', async (organisationId) => {
	const response = await API.get(
		'inspirusapi',
		`/organisations/${organisationId}/verification-requests-notifications`,
		{
			headers: {}
		}
	);
	return response;
});

export const readOrganisation = createAsyncThunk<Organisation, string, { dispatch: AppDispatch; state: RootState }>(
	'organisation/read',

	async (id: string, { getState }) => {
		// check if in user organisations
		const userOrganisations = getState().user.currentUser?.organisations;

		const userOrganisation = userOrganisations?.find((org) => org.id === id);
		if (userOrganisation) {
			return userOrganisation;
		}

		// check if in list
		const list = getState().organisation.list;
		const listOrganisation = list.items.find((org) => org.id === id);
		if (listOrganisation) {
			return listOrganisation;
		}
		// retrieve by id
		const result = await API.get('inspirusapi', `/organisations/${id}`, {
			headers: {},
			response: true,
			queryStringParameters: {}
		});
		const organisation: Organisation = result.data;
		return organisation;
	}
);

export const listOrganisations = createAsyncThunk<
	LazyListResponse<Organisation>,
	VerifierOnlyOrgLazyListRequest,
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/list',

	async ({ next, limit, orgType }) => {
		const result = await API.get('inspirusapi', '/organisations/', {
			headers: {},
			response: true,
			queryStringParameters: { next, limit, orgType }
		});

		return result.data;
	}
);

export const listProspectiveOrganisations = createAsyncThunk<
	LazyListResponse<Organisation>,
	{ type?: string; payload: LazyListRequest; search?: string; startDate?: string; endDate?: string },
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/listProspective',

	async ({ type, payload, search = null, startDate, endDate }) => {
		const queryStringParameters: { [key: string]: any } = {
			next: payload.next,
			limit: payload.limit,
			startDate,
			endDate
		};

		if (search) {
			queryStringParameters.search = search;
		}

		const result = await API.get('inspirusapi', `/organisations/pearsana/prospective/${type}`, {
			headers: {},
			response: true,
			queryStringParameters
		});

		return result.data;
	}
);

export const listOrganisationPreviews = createAsyncThunk<
	OrganisationPreview[],
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/listPreviews',

	async () => {
		const result = await API.get('inspirusapi', '/organisations/previews', {
			headers: {},
			response: true
		});

		return result.data;
	}
);

export const createOrganisation = createAsyncThunk<
	void,
	CreateOrganisationPayload,
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/create',

	async (payload) => {
		const result = await API.post('inspirusapi', '/admins/organisations/', {
			headers: {},
			response: true,
			body: payload
		});

		if (result.data.blockchain) {
			enqueueSnackbar(
				(payload.verifierOnlyOrg ? 'Document Verifier User' : 'Organization Admin User') +
					' successfully stored on the blockchain',
				{ variant: 'success' }
			);
		}

		return result.data.newOrg;
	}
);

export const updateOrganisation = createAsyncThunk<
	UpdateOrganisationPayload,
	UpdateOrganisationPayload,
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/update',

	async (payload) => {
		await API.put('inspirusapi', `/organisations/${payload.id}`, {
			headers: {},
			response: true,
			body: payload
		});

		return payload;
	}
);

export const updatePearsanaRanOrganisation = createAsyncThunk<
	{ id: string; pearsanaRan: boolean },
	{ id: string; pearsanaRan: boolean },
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/pearsanaRan/update',

	async (payload) => {
		await API.put('inspirusapi', `/organisations/pearsanaRan/${payload.id}`, {
			headers: {},
			response: true,
			body: payload
		});

		return payload;
	}
);

export const deleteOrganisation = createAsyncThunk<Organisation, string, { dispatch: AppDispatch; state: RootState }>(
	'organisation/delete',
	async (organisationId) => {
		const response = await API.del('inspirusapi', `/admins/organisations/${organisationId}`, {
			headers: {},
			response: true
		});

		// Dispatch any additional actions or handle state updates as necessary
		return response.data; // Assuming API.del returns the deleted organisation or some confirmation
	}
);

export const createOrganisationProfile = createAsyncThunk<
	OrganisationProfile,
	{ organisationId: string; profile: OrganisationProfile },
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/createProfile',

	async ({ organisationId, profile }) => {
		const response = await API.post('inspirusapi', `/organisations/${organisationId}/profile`, {
			headers: {},
			response: true,
			body: profile
		});

		return response.data;
	}
);

export const updateOrganisationProfile = createAsyncThunk<
	OrganisationProfile,
	{ organisationId: string; id: string; profile: OrganisationProfile },
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/updateProfile',

	async ({ organisationId, id, profile }) => {
		const response = await API.put('inspirusapi', `/organisations/${organisationId}/profile/${id}`, {
			headers: {},
			response: true,
			body: profile
		});

		return response.data;
	}
);

export const readOrganisationProfile = createAsyncThunk<
	OrganisationProfile,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/readProfile', async (organisationId) => {
	const response = await API.get('inspirusapi', `/organisations/${organisationId}/profile`, {
		headers: {},
		response: true
	});

	return response.data;
});

type filteredMetricsParams = {
	organisationId: string;
	scope?: string;
};

export const fetchCandidateInterestsMetrics = createAsyncThunk<
	Organisation,
	filteredMetricsParams,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/metrics/candidate-interests-metrics', async ({ organisationId, scope = 'lastMonth' }) => {
	const response = await API.get(
		'inspirusapi',
		`/organisations/${organisationId}/candidate-interests-metrics?scope=${scope}`,
		{
			headers: {},
			response: true
		}
	);

	return response.data;
});

export const fetchSharedDocumentsRequestsMetrics = createAsyncThunk<
	Organisation,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/metrics/shared-documents-requests-expiry-date-metrics', async (organisationId) => {
	const response = await API.get(
		'inspirusapi',
		`/organisations/${organisationId}/shared-documents-requests-expiry-date-metrics`,
		{
			headers: {},
			response: true
		}
	);
	return response.data;
});

export const fetchSharedDocumentsRequestsOutstandingDocumentMetrics = createAsyncThunk<
	Organisation,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/metrics/shared-documents-requests-outstanding-document-metrics', async (organisationId) => {
	const response = await API.get(
		'inspirusapi',
		`/organisations/${organisationId}/shared-documents-requests-outstanding-document-metrics`,
		{
			headers: {},
			response: true
		}
	);
	return response.data;
});

export const fetchCandidateInterestsRequestOutcomeMetrics = createAsyncThunk<
	Organisation,
	filteredMetricsParams,
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/metrics/candidate-interests-request-outcome-metrics',
	async ({ organisationId, scope = 'currentMonth' }) => {
		const response = await API.get(
			'inspirusapi',
			`/organisations/${organisationId}/candidate-interests-request-outcome-metrics?scope=${scope}`,
			{
				headers: {},
				response: true
			}
		);
		return response.data;
	}
);

export const fetchCandidateInterestsLocationMetrics = createAsyncThunk<
	Organisation,
	filteredMetricsParams,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/metrics/candidate-interests-location-metrics', async ({ organisationId, scope = 'lastMonth' }) => {
	const response = await API.get(
		'inspirusapi',
		`/organisations/${organisationId}/candidate-interests-location-metrics?scope=${scope}`,
		{
			headers: {},
			response: true
		}
	);
	return response.data;
});

export const fetchVerificationRequestsDocumentUploadMetrics = createAsyncThunk<
	Organisation,
	{ organisationId: string; student?: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/metrics/verification-requests-document-upload-metrics', async ({ organisationId, student }) => {
	const url = student
		? `/organisations/${organisationId}/verification-requests-document-upload-metrics?student=${student}`
		: `/organisations/${organisationId}/verification-requests-document-upload-metrics`;
	const response = await API.get('inspirusapi', url, {
		headers: {},
		response: true
	});
	return response.data;
});

export const fetchVerificationRequestsMetrics = createAsyncThunk<
	Organisation,
	{ organisationId: string; student?: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/metrics/verification-requests-metrics', async ({ organisationId, student }) => {
	const url = student
		? `/organisations/${organisationId}/verification-requests-metrics?student=${student}`
		: `/organisations/${organisationId}/verification-requests-metrics`;
	const response = await API.get('inspirusapi', url, {
		headers: {},
		response: true
	});
	return response.data;
});

export const fetchVerificationRequestsAdditionalMetrics = createAsyncThunk<
	Organisation,
	{ organisationId: string; student?: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/metrics/verification-requests-additional-metrics', async ({ organisationId, student }) => {
	const url = student
		? `/organisations/${organisationId}/verification-requests-additional-metrics?student=${student}`
		: `/organisations/${organisationId}/verification-requests-additional-metrics`;
	const response = await API.get('inspirusapi', url, {
		headers: {},
		response: true
	});
	return response.data;
});

export const fetchVerificationRequestsResponseTimeMetrics = createAsyncThunk<
	Organisation,
	{ organisationId: string; student?: string; timeframe?: string },
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/metrics/verification-requests-response-time-metrics',
	async ({ organisationId, student, timeframe }) => {
		const url = student
			? `/organisations/${organisationId}/verification-requests-response-time-metrics?timeframe=${timeframe}&student=${student}`
			: `/organisations/${organisationId}/verification-requests-response-time-metrics?timeframe=${timeframe}`;
		const response = await API.get('inspirusapi', url, {
			headers: {},
			response: true
		});

		return response.data;
	}
);

export const fetchCandidateInterestsCompanyMetrics = createAsyncThunk<
	Organisation,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/metrics/candidate-interests-company-metrics', async (organisationId) => {
	const response = await API.get(
		'inspirusapi',
		`/organisations/${organisationId}/candidate-interests-company-metrics`,
		{
			headers: {},
			response: true
		}
	);
	return response.data;
});

export const listBulkUploadMetrics = createAsyncThunk<
	LazyListResponse<BulkUploadMetric>,
	{ organisationId: string } & LazyListRequest,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/metrics/bulk-upload-metrics', async ({ organisationId, next, limit }) => {
	const response = await API.get('inspirusapi', `/organisations/${organisationId}/bulk-upload-metrics`, {
		headers: {},
		response: true,
		queryStringParameters: { next, limit }
	});
	return response.data;
});

/*
 * Org document history (PTEC-425).
 *
 * Distinct from the per-batch drill-down below, which needs a bulkUploadMetricId and projects
 * only recipient and title. These are flat across the organisation, which is also what makes
 * Partner-API submissions visible — those belong to no batch.
 */
export const listOrganisationUploadedDocuments = createAsyncThunk<
	LazyListResponse<SentDocument>,
	{ organisationId: string } & SentDocumentFilters & LazyListRequest,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/uploaded-documents/list', async ({ organisationId, next, limit, ...filters }) => {
	const queryStringParameters: Record<string, unknown> = { next, limit };

	for (const [key, value] of Object.entries(filters)) {
		// `verified: false` is a real filter, so only undefined and empty strings are dropped.
		// Treating it as falsy would silently widen a search for unverified documents to all.
		if (value !== undefined && value !== '') {
			queryStringParameters[key] = value;
		}
	}

	const response = await API.get('inspirusapi', `/organisations/${organisationId}/uploaded-documents`, {
		headers: {},
		response: true,
		queryStringParameters
	});
	return response.data;
});

/** Documents that never reached a wallet — no document record exists behind these. */
export const listOrganisationFailedSends = createAsyncThunk<
	{ items: FailedSend[]; totalCount: number },
	{ organisationId: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/uploaded-documents/failed', async ({ organisationId }) => {
	const response = await API.get('inspirusapi', `/organisations/${organisationId}/uploaded-documents/failed`, {
		headers: {},
		response: true,
		queryStringParameters: {}
	});
	return response.data;
});

/**
 * Presigns the organisation's own copy of a document it sent.
 *
 * A 404 is expected rather than exceptional: documents sent before this feature shipped have
 * no org-owned copy, because acceptance had already moved and deleted the original. That is
 * reported as `notFound` so the caller can say so specifically, without having to reach into
 * the HTTP response itself.
 */
export const presignUploadedDocumentDownload = createAsyncThunk<
	{ url: string },
	{ organisationId: string; documentId: string },
	{ dispatch: AppDispatch; state: RootState; rejectValue: { notFound: boolean } }
>('organisation/uploaded-documents/download', async ({ organisationId, documentId }, { rejectWithValue }) => {
	try {
		const response = await API.get(
			'inspirusapi',
			`/organisations/${organisationId}/uploaded-documents/${documentId}/download`,
			{ headers: {}, response: true, queryStringParameters: {} }
		);
		return response.data;
	} catch (error: any) {
		return rejectWithValue({ notFound: error?.response?.status === 404 });
	}
});

/**
 * Every organisation on the platform, for the app-admin picker.
 *
 * Deliberately the admin route rather than `listOrganisations`: that one is scoped to what the
 * caller can already see, and an admin moderating the platform needs the full list.
 */
export const listAdminOrganisations = createAsyncThunk<
	LazyListResponse<Organisation>,
	{ limit?: number } | undefined,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/admin/organisations', async (payload) => {
	const response = await API.get('inspirusapi', '/admins/organisations', {
		headers: {},
		response: true,
		queryStringParameters: { limit: payload?.limit ?? 200 }
	});
	return response.data;
});

/**
 * Cross-organisation send history for one recipient, by email.
 *
 * The question support is actually asked is "a document vanished from my wallet" — and the
 * student does not know which organisation sent it. This answers in one query rather than a
 * fan-out across every tenant.
 */
export const listAdminRecipientHistory = createAsyncThunk<
	{ items: SendEvent[] },
	{ email: string; limit?: number },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/admin/recipient-history', async ({ email, limit = 100 }) => {
	const response = await API.get('inspirusapi', '/organisations/admin/recipient-history', {
		headers: {},
		response: true,
		queryStringParameters: { email, limit }
	});
	return response.data;
});

/** Per-send view: when, to whom, how many. */
export const listOrganisationSendHistory = createAsyncThunk<
	LazyListResponse<SendEvent>,
	{
		organisationId: string;
		recipientUserId?: string;
		uploadMethod?: string;
		dateFrom?: string;
		dateTo?: string;
	} & LazyListRequest,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/send-history/list', async ({ organisationId, next, limit, ...filters }) => {
	const queryStringParameters: Record<string, unknown> = { next, limit };
	for (const [key, value] of Object.entries(filters)) {
		if (value !== undefined && value !== '') {
			queryStringParameters[key] = value;
		}
	}

	const response = await API.get('inspirusapi', `/organisations/${organisationId}/send-history`, {
		headers: {},
		response: true,
		queryStringParameters
	});
	return response.data;
});

/**
 * Withdraws a document the organisation sent.
 *
 * `reason` is required and is shown to the recipient verbatim. The API rejects a request
 * without one rather than defaulting it: a document disappearing from someone's wallet with
 * no explanation is the failure this whole flow exists to avoid.
 */
export const cancelUploadedDocument = createAsyncThunk<
	{ documentId: string; previousStatus: string },
	{ organisationId: string; documentId: string; reason: string; note?: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/uploaded-documents/cancel', async ({ organisationId, documentId, reason, note }) => {
	const response = await API.del('inspirusapi', `/documents/org-uploads/${organisationId}/${documentId}`, {
		headers: {},
		response: true,
		body: { reason, ...(note ? { note } : {}) }
	});
	return response.data;
});

/**
 * Puts a replacement file somewhere the replace endpoint can read it.
 *
 * Deliberately not `/documents/presigned-upload`: that route presigns into the *recipient's*
 * protected prefix and is gated on `is_family_admin_of_user`, so an organisation is refused
 * by design — it exists for a parent uploading into their child's wallet.
 *
 * Uploads with the caller's own credentials instead, the same way bulk upload does, and
 * returns the **full** bucket key. `Storage.put` reports only the key relative to the level
 * prefix, while the replace endpoint does `head_object` on the key it is handed, so the
 * identity prefix has to be resolved here or the object is never found.
 */
export const uploadReplacementDocument = createAsyncThunk<
	{ key: string },
	{ organisationId: string; file: File },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/uploaded-documents/upload-replacement', async ({ organisationId, file }) => {
	const relativeKey = `orgDocumentReplacement/${organisationId}/${Date.now()}-${file.name}`;

	await Storage.put(relativeKey, file, {
		level: 'private',
		contentType: file.type || 'application/octet-stream'
	});

	const { identityId } = await Auth.currentCredentials();
	return { key: `private/${identityId}/${relativeKey}` };
});

/**
 * Swaps the file behind a document already sent. The document keeps its id, so the recipient
 * sees a correction rather than a second document competing with the first.
 *
 * The replacement is uploaded to S3 first; this passes the resulting key.
 */
export const replaceUploadedDocument = createAsyncThunk<
	{ documentId: string; verificationRevoked: boolean; sharesWithdrawn: number },
	{ organisationId: string; documentId: string; key: string; title?: string; reason: string; note?: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/uploaded-documents/replace', async ({ organisationId, documentId, ...body }) => {
	const response = await API.put('inspirusapi', `/documents/org-uploads/${organisationId}/${documentId}/replace`, {
		headers: {},
		response: true,
		body
	});
	return response.data;
});

/** Sets or changes the verification this organisation applied to a document it sent. */
export const setUploadedDocumentVerification = createAsyncThunk<
	{ documentId: string; status: string; intent: string },
	{ organisationId: string; documentId: string; intent: string; reason?: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/uploaded-documents/set-verification', async ({ organisationId, documentId, ...body }) => {
	const response = await API.put(
		'inspirusapi',
		`/organisations/${organisationId}/uploaded-documents/${documentId}/verification`,
		{ headers: {}, response: true, body }
	);
	return response.data;
});

/**
 * Changes what the recipient may do with a document this organisation sent (PTEC-896).
 *
 * Turning sharing off also withdraws the shares the recipient already made of it, and the
 * response says how many.
 */
export const setUploadedDocumentAccess = createAsyncThunk<
	{ documentId: string; changed: boolean; sharesWithdrawn: number; viewable: boolean; shareable: boolean },
	{ organisationId: string; documentId: string; viewable: boolean; shareable: boolean; note?: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/uploaded-documents/set-access', async ({ organisationId, documentId, ...body }) => {
	const response = await API.put('inspirusapi', `/documents/org-uploads/${organisationId}/${documentId}/access`, {
		headers: {},
		response: true,
		body
	});
	return response.data;
});

/**
 * Revokes the verification this organisation applied.
 *
 * The credential-integrity case: a qualification issued in error stays in circulation until
 * somebody withdraws it, and until now nobody could.
 */
export const revokeUploadedDocumentVerification = createAsyncThunk<
	{ documentId: string; status: string },
	{ organisationId: string; documentId: string; reason?: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/uploaded-documents/revoke-verification', async ({ organisationId, documentId, reason }) => {
	const response = await API.del(
		'inspirusapi',
		`/organisations/${organisationId}/uploaded-documents/${documentId}/verification`,
		{ headers: {}, response: true, body: reason ? { reason } : {} }
	);
	return response.data;
});

/**
 * Withdraws every document in one upload.
 *
 * `confirmedCount` is required above the server's threshold and must match exactly — which
 * catches the batch having grown since the dialog opened, or the wrong upload being selected.
 * Not atomic: the response reports per-document outcomes rather than implying a transaction.
 */
export const retractSendBatch = createAsyncThunk<
	{
		batchId: string;
		total: number;
		withdrawn: number;
		failed: number;
		skipped: number;
		failures: { documentId: string; message: string }[];
	},
	{ organisationId: string; batchId: string; reason: string; note?: string; confirmedCount?: number },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/send-batches/retract', async ({ organisationId, batchId, ...body }) => {
	const response = await API.del('inspirusapi', `/documents/org-upload-batches/${organisationId}/${batchId}`, {
		headers: {},
		response: true,
		body
	});
	return response.data;
});

export const listBulkUploadDocumentMetrics = createAsyncThunk<
	LazyListResponse<BulkUploadDocumentMetric>,
	{ organisationId: string; bulkUploadMetricId: string; status?: string } & LazyListRequest,
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/metrics/bulk-upload-document-metrics',
	async ({ organisationId, bulkUploadMetricId, next, limit, status }) => {
		// Construct query string parameters
		const queryStringParameters: Record<string, any> = { next, limit };
		if (status) {
			queryStringParameters.status = status;
		}

		const response = await API.get(
			'inspirusapi',
			`/organisations/${organisationId}/bulk-upload-metrics/${bulkUploadMetricId}`,
			{
				headers: {},
				response: true,
				queryStringParameters
			}
		);
		return response.data;
	}
);

export const fetchPearsanaCandidateInterestsMetrics = createAsyncThunk<
	Organisation,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/metrics/pearsana-candidate-interests-metrics', async (organisationId) => {
	const response = await API.get(
		'inspirusapi',
		`/organisations/${organisationId}/pearsana-candidate-interests-metrics`,
		{
			headers: {},
			response: true
		}
	);
	return response.data;
});

export const fetchPearsanaCandidateInterestsLocationMetrics = createAsyncThunk<
	Organisation,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/metrics/pearsana-candidate-interests-location-metrics', async (organisationId) => {
	const response = await API.get(
		'inspirusapi',
		`/organisations/${organisationId}/pearsana-candidate-interests-location-metrics`,
		{
			headers: {},
			response: true
		}
	);
	return response.data;
});

export const createOrgAdvert = createAsyncThunk<
	OrgAdvertisement,
	{ organisationId: string; profileId: string; formData: any },
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/adverts/create',

	async ({ organisationId, profileId, formData }) => {
		const result = await API.post(
			'inspirusapi',
			`/organisations/${organisationId}/profile/${profileId}/spotlights`,
			{
				headers: {},
				response: true,
				body: formData
			}
		);

		return result.data;
	}
);

export const updateOrgAdvert = createAsyncThunk<
	OrgAdvertisement,
	{ organisationId: string; advertId: string; profileId: string; formData: any },
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/adverts/update',

	async ({ organisationId, advertId, profileId, formData }) => {
		const result = await API.post(
			'inspirusapi',
			`/organisations/${organisationId}/profile/${profileId}/spotlights/${advertId}`,
			{
				headers: {},
				response: true,
				body: formData
			}
		);

		return result.data;
	}
);

export const readOrgAdverts = createAsyncThunk<
	LazyListResponse<ReadOrgAdvert>,
	{ organisationId: string } & LazyListRequest,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/adverts/', async ({ organisationId, next, limit }) => {
	const response = await API.get('inspirusapi', `/organisations/${organisationId}/adverts`, {
		headers: {},
		response: true,
		queryStringParameters: {
			limit,
			next
		}
	});

	return response.data;
});

export const deleteOrgAdvert = createAsyncThunk<
	void,
	{ organisationId: string; advertId: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/adverts/delete', async ({ organisationId, advertId }) => {
	await API.del('inspirusapi', `/organisations/${organisationId}/spotlights/${advertId}`, {
		headers: {},
		response: true
	});
});

export const readSingleOrgAdvert = createAsyncThunk<
	ReadSingleOrgAdvert,
	{ organisationId: string; advertId: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/advert/', async ({ advertId }) => {
	const response = await API.get('inspirusapi', `/adverts/spotlights/${advertId}`, {
		headers: {},
		response: true
	});

	return response.data;
});

export const fetchSpotlightCount = createAsyncThunk<number, string, { dispatch: AppDispatch; state: RootState }>(
	'organisation/spotlight/count',
	async (organisationalId) => {
		const response = await API.get('inspirusapi', `/adverts/${organisationalId}/spotlights/count`, {
			headers: {},
			response: true
		});

		return response.data;
	}
);

export const listStudents = createAsyncThunk<
	LazyListResponse<StudentList>,
	{ organisationId: string; advertId: string } & LazyListRequest,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/students/', async ({ organisationId, advertId, next, limit }) => {
	const response = await API.get('inspirusapi', `/organisations/${organisationId}/students/${advertId}`, {
		headers: {},
		response: true,
		queryStringParameters: {
			limit,
			next
		}
	});

	return response.data;
});

export const updateOrganisationGrant = createAsyncThunk<
	any,
	{ grantId: string; grant: any },
	{ dispatch: AppDispatch; state: RootState }
>('organisations/updateOrganisationGrant', async ({ grantId, grant }) => {
	try {
		const response = await API.put('inspirusapi', `/organisations/grants/${grantId}`, {
			headers: {},
			response: true,
			body: grant
		});

		return response;
	} catch (error: unknown) {
		return 'Failed to update organization grant.';
	}
});

export const getActiveSubscriptions = createAsyncThunk<
	any,
	{ organisationId: string } & LazyListRequest,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/activeSubscriptions', async ({ organisationId, next, limit }) => {
	const response = await API.get('inspirusapi', `/organisations/${organisationId}/subscriptions`, {
		headers: {},
		response: true,
		queryStringParameters: { next, limit }
	});
	return response.data;
});

export const cancelActiveSubscription = createAsyncThunk<
	void,
	{ organisationId: string; subscriptionId: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/cancelActiveSubscription', async ({ organisationId, subscriptionId }) => {
	await API.del('inspirusapi', `/organisations/${organisationId}/subscriptions/${subscriptionId}`, {
		headers: {},
		response: true
	});
});

export const listFamilyAdmins = createAsyncThunk<
	User[],
	{ organisationId: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/listFamilyAdmins', async ({ organisationId }) => {
	const result = await API.get('inspirusapi', `/organisations/${organisationId}/users`, {
		headers: {},
		response: true,
		queryStringParameters: {
			roleName: RoleName.FamilyOrganisationAdmin,
			organisationAdminView: true
		}
	});
	return result.data?.items ?? [];
});

export const listFamilyChildren = createAsyncThunk<
	User[],
	{ organisationId: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/listFamilyChildren', async ({ organisationId }) => {
	const result = await API.get('inspirusapi', `/organisations/${organisationId}/users`, {
		headers: {},
		response: true,
		queryStringParameters: {
			roleName: RoleName.FamilyMember,
			organisationAdminView: true
		}
	});
	return result.data?.items ?? [];
});

// Fetch all family member notification counts
export const listFamilyMemberNotifications = createAsyncThunk<
	{ members: Array<{ userId: string; notificationCount: number }> },
	string, // organisationId
	{ dispatch: AppDispatch; state: RootState }
>('organisation/listFamilyMemberNotifications', async (organisationId) => {
	const response = await API.get('inspirusapi', `/organisations/${organisationId}/family-member-notifications`, {
		headers: {}
	});
	return response;
});

// Delete all notifications for a specific family member
export const dismissFamilyMemberNotifications = createAsyncThunk<
	{ userId: string },
	{ organisationId: string; userId: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/dismissFamilyMemberNotifications', async ({ organisationId, userId }) => {
	await API.del('inspirusapi', `/organisations/${organisationId}/family-member-notifications/${userId}`, {
		headers: {},
		response: true
	});
	return { userId };
});

// Fetch an organisation for the verification info dialog without overwriting state.current
export const readVerifyingOrganisation = createAsyncThunk<
	Organisation,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/readVerifyingOrganisation', async (id: string) => {
	const result = await API.get('inspirusapi', `/organisations/${id}`, {
		headers: {},
		response: true,
		queryStringParameters: {}
	});
	return result.data;
});

// ── App Clients ─────────────────────────────────────────────────────────────

export const listAppClients = createAsyncThunk<
	{ items: AppClient[] },
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/listAppClients', async (organisationId, { rejectWithValue }) => {
	try {
		const response = await API.get('inspirusapi', `/organisations/${organisationId}/app-clients`, {
			headers: {},
			response: true
		});
		return response.data;
	} catch (error: any) {
		console.error('[listAppClients]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

export const createAppClient = createAsyncThunk<
	CreateAppClientResponse,
	{ organisationId: string; name: string; description?: string; scopes: string[] },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/createAppClient', async ({ organisationId, name, description, scopes }, { rejectWithValue }) => {
	try {
		const response = await API.post('inspirusapi', `/organisations/${organisationId}/app-clients`, {
			headers: {},
			response: true,
			body: { name, description, scopes }
		});
		return response.data;
	} catch (error: any) {
		console.error('[createAppClient]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

export const deleteAppClient = createAsyncThunk<
	{ clientId: string },
	{ organisationId: string; clientId: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/deleteAppClient', async ({ organisationId, clientId }, { rejectWithValue }) => {
	try {
		await API.del('inspirusapi', `/organisations/${organisationId}/app-clients/${clientId}`, {
			headers: {},
			response: true
		});
		return { clientId };
	} catch (error: any) {
		console.error('[deleteAppClient]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

// ── Organisation Constituents ───────────────────────────────────────────────

/**
 * Partner configuration: the API namespace the organisation addresses its
 * constituents under, and the licence it nominates for them at onboarding.
 * App Admin only — the PUT is authorised server-side.
 */
export const getPartnerConfig = createAsyncThunk<
	{ partnerConfig: PartnerConfig | null },
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/getPartnerConfig', async (organisationId, { rejectWithValue }) => {
	try {
		const response = await API.get('inspirusapi', `/organisations/${organisationId}/partner-config`, {
			headers: {},
			response: true
		});
		return response.data;
	} catch (error: any) {
		console.error('[getPartnerConfig]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

export const savePartnerConfig = createAsyncThunk<
	{ partnerConfig: PartnerConfig },
	{ organisationId: string; namespace: string; licenseProductId?: string | null },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/savePartnerConfig', async ({ organisationId, namespace, licenseProductId }, { rejectWithValue }) => {
	try {
		const response = await API.put('inspirusapi', `/organisations/${organisationId}/partner-config`, {
			headers: {},
			response: true,
			body: { namespace, licenseProductId: licenseProductId ?? null }
		});
		return response.data;
	} catch (error: any) {
		console.error('[savePartnerConfig]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

export const listConstituents = createAsyncThunk<
	{
		items: OrganisationConstituent[];
		nextToken?: string | null;
		append?: boolean;
		organisationId?: string;
		search?: string;
		truncated?: boolean;
	},
	| string
	| {
			organisationId: string;
			limit?: number;
			nextToken?: string | null;
			append?: boolean;
			search?: string;
	  },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/listConstituents', async (arg, { dispatch, rejectWithValue }) => {
	const {
		organisationId,
		limit,
		nextToken,
		append,
		search
	}: {
		organisationId: string;
		limit?: number;
		nextToken?: string | null;
		append?: boolean;
		search?: string;
	} = typeof arg === 'string' ? { organisationId: arg } : arg;

	// Declare what this request is for BEFORE issuing it, so the reducer can discard
	// anything that arrives after the view has moved on.
	dispatch(setConstituentScope({ organisationId, search: search ?? '' }));
	try {
		// Paged: a 200k-row roster cannot be fetched whole.
		const query = new URLSearchParams();
		if (limit) {
			query.set('limit', String(limit));
		}
		if (nextToken) {
			query.set('nextToken', nextToken);
		}
		// Searched server-side so it covers the whole roster, not just what is loaded.
		if (search) {
			query.set('search', search);
		}
		const suffix = query.toString() ? `?${query.toString()}` : '';

		const response = await API.get('inspirusapi', `/organisations/${organisationId}/constituents${suffix}`, {
			headers: {},
			response: true
		});
		// `append` and `organisationId` are echoed back so the reducer can tell an
		// extending page from a replacing one, and can drop a response that arrived
		// after the admin moved to a different organisation.
		return { ...response.data, append, organisationId, search: search ?? '' };
	} catch (error: any) {
		console.error('[listConstituents]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

/**
 * Step 1 — reserve a job and get somewhere to put the file.
 *
 * Nothing is queued by this call. The planner must not be able to run before the
 * browser has finished uploading, so the work is released by `startConstituentImport`
 * only once the object is known to exist.
 */
export const createConstituentImport = createAsyncThunk<
	ConstituentImportTicket,
	{ organisationId: string; fileName: string; totalRows: number },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/createConstituentImport', async ({ organisationId, fileName, totalRows }, { rejectWithValue }) => {
	try {
		const response = await API.post('inspirusapi', `/organisations/${organisationId}/constituents/imports`, {
			headers: {},
			response: true,
			body: { fileName, totalRows }
		});
		return response.data;
	} catch (error: any) {
		console.error('[createConstituentImport]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

/**
 * Step 2 — PUT the file straight to S3.
 *
 * Deliberately a bare `fetch`, not the Amplify client: the URL is presigned, so
 * adding an Authorization header would break the signature. `imports/` is not one
 * of Amplify Storage's managed prefixes either, so `Storage.put` could not write
 * here at all.
 */
export const uploadConstituentFile = createAsyncThunk<
	void,
	{ uploadUrl: string; file: File },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/uploadConstituentFile', async ({ uploadUrl, file }, { rejectWithValue }) => {
	try {
		const response = await fetch(uploadUrl, { method: 'PUT', body: file });
		if (!response.ok) {
			throw new Error(`Upload failed with status ${response.status}`);
		}
	} catch (error: any) {
		console.error('[uploadConstituentFile]', error);
		return rejectWithValue({ message: error.message });
	}
});

/**
 * Step 3 — release the job to the queue.
 *
 * Idempotent server-side, so a retry after a flaky network cannot fan the import
 * out twice.
 */
export const startConstituentImport = createAsyncThunk<
	ConstituentImportJob,
	{ organisationId: string; jobId: string; fileName?: string; totalRows?: number },
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/startConstituentImport',
	async ({ organisationId, jobId, fileName, totalRows }, { rejectWithValue }) => {
		try {
			const response = await API.post(
				'inspirusapi',
				`/organisations/${organisationId}/constituents/imports/${jobId}/start`,
				{ headers: {}, response: true, body: { fileName, totalRows } }
			);
			return response.data;
		} catch (error: any) {
			console.error('[startConstituentImport]', error);
			return rejectWithValue(error?.response?.data ?? { message: error.message });
		}
	}
);

/** Progress for one import, polled while it is active. */
export const pollConstituentImport = createAsyncThunk<
	ConstituentImportJob,
	{ organisationId: string; jobId: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/pollConstituentImport', async ({ organisationId, jobId }, { rejectWithValue }) => {
	try {
		const response = await API.get(
			'inspirusapi',
			`/organisations/${organisationId}/constituents/imports/${jobId}`,
			{ headers: {}, response: true }
		);
		return response.data;
	} catch (error: any) {
		console.error('[pollConstituentImport]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

/** Import history for the dashboard, newest first. */
export const listConstituentImports = createAsyncThunk<
	{ items: ConstituentImportJob[] },
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/listConstituentImports', async (organisationId, { rejectWithValue }) => {
	try {
		const response = await API.get('inspirusapi', `/organisations/${organisationId}/constituents/imports`, {
			headers: {},
			response: true
		});
		return response.data;
	} catch (error: any) {
		console.error('[listConstituentImports]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

export const deleteConstituent = createAsyncThunk<
	{ constituentId: string },
	{ organisationId: string; constituentId: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/deleteConstituent', async ({ organisationId, constituentId }, { rejectWithValue }) => {
	try {
		await API.del('inspirusapi', `/organisations/${organisationId}/constituents/${constituentId}`, {
			headers: {},
			response: true
		});
		return { constituentId };
	} catch (error: any) {
		console.error('[deleteConstituent]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

/**
 * The roster's true size, independent of what is paged in.
 *
 * Kept separate from the list so the table renders immediately while this
 * resolves: at 200k the count is a partition read.
 */
export const fetchConstituentCount = createAsyncThunk<
	{ total: number; organisationId: string },
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/fetchConstituentCount', async (organisationId, { rejectWithValue }) => {
	try {
		const response = await API.get('inspirusapi', `/organisations/${organisationId}/constituents/count`, {
			headers: {},
			response: true
		});
		return { ...response.data, organisationId };
	} catch (error: any) {
		console.error('[fetchConstituentCount]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

export const purgeConstituents = createAsyncThunk<
	ConstituentImportJob,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/purgeConstituents', async (organisationId, { rejectWithValue }) => {
	try {
		// 202 and a job: erasing a 200k roster cannot finish inside a request, so the
		// dashboard polls this the same way it polls an import.
		const response = await API.del('inspirusapi', `/organisations/${organisationId}/constituents`, {
			headers: {},
			response: true
		});
		return response.data;
	} catch (error: any) {
		console.error('[purgeConstituents]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

// ─── Student bulk upload (PTEC-975) and the Students table (PTEC-973) ──────────

const studentImportsPath = (organisationId: string) => `/organisations/${organisationId}/student-imports`;
const studentsPath = (organisationId: string) => `/organisations/${organisationId}/students`;

/** The upload limits, the mode and the wallets left, for the dashboard widget and its dialog. */
export const fetchStudentImportSettings = createAsyncThunk<
	StudentImportSettings,
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/fetchStudentImportSettings', async (organisationId, { rejectWithValue }) => {
	try {
		return await API.get('inspirusapi', `${studentImportsPath(organisationId)}/settings`, {});
	} catch (error: any) {
		console.error('[fetchStudentImportSettings]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

/**
 * Reserves an upload and returns the link to put the file at. Refused, with the API's `code`,
 * over the row limit, while the fallback switch is on, or while another upload is running.
 */
export const createStudentImport = createAsyncThunk<
	StudentImportTicket,
	{ organisationId: string; fileName: string; rowCount: number },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/createStudentImport', async ({ organisationId, fileName, rowCount }, { rejectWithValue }) => {
	try {
		return await API.post('inspirusapi', studentImportsPath(organisationId), { body: { fileName, rowCount } });
	} catch (error: any) {
		console.error('[createStudentImport]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

/**
 * PUTs the file to the link the API signed. A bare `fetch`, as for constituent imports: an
 * Authorization header would break the signature, and `student-imports/` is not a Storage prefix.
 */
export const uploadStudentFile = createAsyncThunk<
	void,
	{ uploadUrl: string; file: File },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/uploadStudentFile', async ({ uploadUrl, file }, { rejectWithValue }) => {
	try {
		const response = await fetch(uploadUrl, { method: 'PUT', body: file });
		if (!response.ok) {
			throw new Error(`Upload failed with status ${response.status}`);
		}
	} catch (error: any) {
		console.error('[uploadStudentFile]', error);
		return rejectWithValue({ message: error.message });
	}
});

/** One upload, polled while it runs. */
export const fetchStudentImport = createAsyncThunk<
	StudentImportJob,
	{ organisationId: string; jobId: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/fetchStudentImport', async ({ organisationId, jobId }, { rejectWithValue }) => {
	try {
		return await API.get('inspirusapi', `${studentImportsPath(organisationId)}/${jobId}`, {});
	} catch (error: any) {
		console.error('[fetchStudentImport]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

/** The organisation's upload history, newest first. */
export const listStudentImports = createAsyncThunk<
	StudentImportJob[],
	string,
	{ dispatch: AppDispatch; state: RootState }
>('organisation/listStudentImports', async (organisationId, { rejectWithValue }) => {
	try {
		const response = await API.get('inspirusapi', studentImportsPath(organisationId), {});
		return response?.items ?? [];
	} catch (error: any) {
		console.error('[listStudentImports]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

/** Every row of an upload with what happened to it, for the results file. */
export const listStudentImportRows = createAsyncThunk<
	StudentImportRow[],
	{ organisationId: string; jobId: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/listStudentImportRows', async ({ organisationId, jobId }, { rejectWithValue }) => {
	try {
		const response = await API.get('inspirusapi', `${studentImportsPath(organisationId)}/${jobId}/rows`, {});
		return response?.items ?? [];
	} catch (error: any) {
		console.error('[listStudentImportRows]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

/** Frees the organisation's upload slot after its file could not be sent. */
export const abandonStudentImport = createAsyncThunk<
	StudentImportJob,
	{ organisationId: string; jobId: string },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/abandonStudentImport', async ({ organisationId, jobId }, { rejectWithValue }) => {
	try {
		return await API.post('inspirusapi', `${studentImportsPath(organisationId)}/${jobId}/abandon`, {});
	} catch (error: any) {
		console.error('[abandonStudentImport]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

/** A search as the API reads it: only what is set, and `refresh` as 1. */
const studentSearchQuery = ({ refresh, ...search }: Partial<StudentSearch>) => {
	const query: Record<string, string> = {};
	Object.entries(search).forEach(([key, value]) => {
		if (value !== undefined && value !== '') query[key] = String(value);
	});
	if (refresh) query.refresh = '1';
	return query;
};

/** One page of the organisation's students matching a search, filters and sort. */
export const searchStudents = createAsyncThunk<
	StudentSearchResult,
	{ organisationId: string; search: StudentSearch },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/searchStudents', async ({ organisationId, search }, { rejectWithValue }) => {
	try {
		return await API.get('inspirusapi', studentsPath(organisationId), {
			queryStringParameters: studentSearchQuery(search)
		});
	} catch (error: any) {
		console.error('[searchStudents]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

/** Everyone matching a search who has not finished signing up (at most 5,000), for "resend to all". */
export const listPendingStudentIds = createAsyncThunk<
	{ ids: string[]; totalCount: number },
	{ organisationId: string; search: Pick<StudentSearch, 'q' | 'agentId'> },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/listPendingStudentIds', async ({ organisationId, search }, { rejectWithValue }) => {
	try {
		return await API.get('inspirusapi', `${studentsPath(organisationId)}/pending-ids`, {
			queryStringParameters: studentSearchQuery(search)
		});
	} catch (error: any) {
		console.error('[listPendingStudentIds]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

/** How many students one resend request may carry; a longer list is sent in batches. */
export const RESEND_BATCH_SIZE = 25;

/** Resends the invitation to up to RESEND_BATCH_SIZE students, and says what happened to each. */
export const resendStudentInvites = createAsyncThunk<
	{ results: { userId: string; outcome: ResendOutcome }[] },
	{ organisationId: string; userIds: string[] },
	{ dispatch: AppDispatch; state: RootState }
>('organisation/resendStudentInvites', async ({ organisationId, userIds }, { rejectWithValue }) => {
	try {
		return await API.post('inspirusapi', `${studentsPath(organisationId)}/resend-invites`, { body: { userIds } });
	} catch (error: any) {
		console.error('[resendStudentInvites]', error);
		return rejectWithValue(error?.response?.data ?? { message: error.message });
	}
});

// ── Partner API Logs ────────────────────────────────────────────────────────

export const fetchPartnerApiLogs = createAsyncThunk<
	{ logs: PartnerApiLog[]; nextToken?: string; append?: boolean },
	FetchPartnerApiLogsParams,
	{ dispatch: AppDispatch; state: RootState }
>(
	'organisation/fetchPartnerApiLogs',
	async ({ organisationId, hours, status, limit, nextToken, startTime, endTime, append }, { rejectWithValue }) => {
		const queryStringParameters: Record<string, string | number> = {};
		if (hours) queryStringParameters.hours = hours;
		if (status) queryStringParameters.status = status;
		if (limit) queryStringParameters.limit = limit;
		if (nextToken) queryStringParameters.nextToken = nextToken;
		if (startTime) queryStringParameters.startTime = startTime;
		if (endTime) queryStringParameters.endTime = endTime;
		try {
			const response = await API.get('inspirusapi', `/organisations/${organisationId}/partner-api-logs`, {
				headers: {},
				response: true,
				queryStringParameters
			});
			return { ...response.data, append };
		} catch (error: any) {
			console.error('[fetchPartnerApiLogs]', error);
			return rejectWithValue(error?.response?.data ?? { message: error.message });
		}
	}
);

const organisationSlice = createSlice({
	name: 'organisation',
	initialState,
	reducers: {
		/**
		 * Declare what the roster state is currently meant to show.
		 *
		 * Dispatched by `listConstituents` before it issues its request, so responses
		 * that arrive after the admin has changed organisation or typed a new search
		 * can be recognised as stale and discarded rather than repainting the table.
		 */
		setConstituentScope: (state, action: PayloadAction<{ organisationId: string; search: string }>) => {
			// Moving to a different organisation invalidates the loaded roster, not just
			// future responses. Leaving the rows and the TOTAL in place meant the purge
			// confirmation could quote the previous organisation's size — "Remove all
			// 10,000 constituents?" — while the request deleted this one's.
			if (state.constituentsScope.organisationId !== action.payload.organisationId) {
				state.constituents = [];
				state.constituentsNextToken = null;
				state.constituentsTotal = null;
				state.constituentsTruncated = false;
				state.constituentImport = null;
			}
			state.constituentsScope = action.payload;
		},
		resetOrganisation: (state) => {
			state.current = undefined;
			state.list = createLazyList();
			state.prospectiveList = createLazyList();
			state.prospectiveIndividuals = createLazyList();
			state.portaLogs = createLazyList();
			state.detailedPortaLogs = createLazyList();
			state.candidate_interests_notifications = null;
			state.verification_requests_notifications = null;
			state.shared_wallet_requests_notification_count = null;
			state.metrics = {};
			state.organisationProfileList = {};
			state.adverts = createLazyList();
			state.singleAdvert = createLazyList();
			state.spotlightCount = 0;
			state.previews = { all: [] };
			state.readOrganisationRequest = createRequestStatusObject();
			state.prospectiveOrganisationRequest = createRequestStatusObject();
			state.prospectiveIndividualRequest = createRequestStatusObject();
			state.listPortalLogsRequest = createRequestStatusObject();
			state.listDetailedPortalLogsRequest = createRequestStatusObject();
			state.listOrganisationsRequest = createRequestStatusObject();
			state.createOrganisationRequest = createRequestStatusObject();
			state.updateOrganisationRequest = createRequestStatusObject();
			state.deleteOrganisationRequest = createRequestStatusObject();
			state.fetchSpotlightCountRequest = createRequestStatusObject();
			state.createOrganisationProfileRequest = createRequestStatusObject();
			state.updateOrganisationProfileRequest = createRequestStatusObject();
			state.readOrganisationProfileRequest = createRequestStatusObject();
			state.fetchCandidateInterestsMetrics = createRequestStatusObject();
			state.fetchSharedDocumentsRequestsMetrics = createRequestStatusObject();
			state.fetchSharedDocumentsRequestsOutstandingDocumentMetrics = createRequestStatusObject();
			state.fetchCandidateInterestsRequestOutcomeMetrics = createRequestStatusObject();
			state.fetchCandidateInterestsLocationMetrics = createRequestStatusObject();
			state.fetchVerificationRequestsDocumentUploadMetrics = createRequestStatusObject();
			state.fetchVerificationRequestsMetrics = createRequestStatusObject();
			state.fetchVerificationRequestsResponseTimeMetrics = createRequestStatusObject();
			state.fetchVerificationRequestsAdditionalMetrics = createRequestStatusObject();
			state.fetchCandidateInterestsCompanyMetrics = createRequestStatusObject();
			state.listBulkUploadMetrics = createRequestStatusObject();
			state.listBulkUploadDocumentMetrics = createRequestStatusObject();
			state.fetchPearsanaCandidateInterestsMetrics = createRequestStatusObject();
			state.fetchPearsanaCandidateInterestsLocationMetrics = createRequestStatusObject();
			state.listCandidatInterestsNotifications = createRequestStatusObject();
			state.listDocumentRequestNotificationCount = createRequestStatusObject();
			state.listVerificationRequestNotifications = createRequestStatusObject();
			state.createOrgAdvertRequest = createRequestStatusObject();
			state.updateOrgAdvertRequest = createRequestStatusObject();
			state.deleteOrgAdvertRequest = createRequestStatusObject();
			state.readOrgAdverts = createRequestStatusObject();
			state.readSingleOrgAdvert = createRequestStatusObject();
			state.cancelActiveSubscriptionRequest = createRequestStatusObject();
			state.organisationActiveSubscriptions = createLazyList();
			state.organisationActiveSubscriptionsRequest = createRequestStatusObject();
			state.familyAdmins = [];
			state.familyChildren = [];
			state.listFamilyAdminsRequest = createRequestStatusObject();
			state.listFamilyChildrenRequest = createRequestStatusObject();
			state.family_member_notifications = {};
			state.listFamilyMemberNotificationsRequest = createRequestStatusObject();
			state.dismissFamilyMemberNotificationsRequest = createRequestStatusObject();
			state.verifyingOrganisation = undefined;
			state.readVerifyingOrganisationRequest = createRequestStatusObject();
			state.appClients = [];
			state.appClientReadStatus = createRequestStatusObject();
			state.createAppClientRequest = createRequestStatusObject();
			state.deleteAppClientRequest = createRequestStatusObject();
			state.partnerApiLogs = { logs: [], nextToken: null };
			state.partnerApiLogsRequest = createRequestStatusObject();
		},
		addNewOrgToList: (state, action) => {
			if (state.list.items.length < 10) {
				state.list.items.push(action.payload.payload);
			}
		}
	},
	extraReducers: (builder) => {
		buildReducers(builder, updateOrganisationGrant, 'updateOrganisationGrantStatus', {});
		buildReducers(builder, readOrganisation, 'readOrganisationRequest', {
			onFulfilled: (state, payload) => {
				state.current = payload;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, listOrganisations, 'readOrganisationRequest', {
			onFulfilled: (state, payload) => {
				const list = state.list;
				list.items = payload.items;
				list.next = payload.next;
				list.totalCount = payload.totalCount;
			}
		});
		buildReducers(builder, listProspectiveOrganisations, 'prospectiveOrganisationRequest', {
			onFulfilled: (state, payload) => {
				const prospectiveList = state.prospectiveList;
				prospectiveList.items = payload.items;
				prospectiveList.next = payload.next;
				prospectiveList.totalCount = payload.totalCount;
			}
		});
		buildReducers(builder, listOrganisationPreviews, 'listOrganisationsRequest', {
			onFulfilled: (state, payload) => {
				payload.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
				const orgs: {
					all: OrganisationPreview[];
				} & {
					[key in OrganisationType]?: OrganisationPreview[];
				} = { all: payload };
				payload.forEach((org) => {
					if (!orgs[org.type]) {
						orgs[org.type] = [];
					}
					orgs[org.type]?.push(org);
				});
				state.previews = orgs;
			}
		});
		buildReducers(builder, createOrganisation, 'createOrganisationRequest', {});
		buildReducers(builder, updateOrganisation, 'updateOrganisationRequest', {
			onFulfilled: (state, payload) => {
				state.current = { ...state.current, ...payload };
				enqueueSnackbar(i18n.t('pages.organisation.dashboard.updateDetails.successMessage'), {
					variant: 'success'
				});
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.organisation.dashboard.updateDetails.errorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, updatePearsanaRanOrganisation, 'updateOrganisationRequest', {
			onFulfilled: (state, payload) => {
				const { id, pearsanaRan } = payload;
				const org = state.list.items.find((org: { id: string }) => org.id === id);
				if (org) {
					org.pearsanaRan = pearsanaRan;
				}
				state.isLoading = false;
				enqueueSnackbar(i18n.t('pages.organisation.dashboard.updateDetails.successMessage'), {
					variant: 'success'
				});
			},
			onPending: (state) => {
				state.isLoading = true;
			},
			onRejected: (state) => {
				enqueueSnackbar(i18n.t('pages.organisation.dashboard.updateDetails.errorMessage'), {
					variant: 'error'
				});
				state.isLoading = false;
			}
		});
		buildReducers(builder, deleteOrganisation, 'deleteOrganisationRequest', {
			onFulfilled: () => {
				enqueueSnackbar('Organization deleted successfully', { variant: 'success' });
			},
			onRejected: (error) => {
				enqueueSnackbar(error.message || 'Failed to delete organization', { variant: 'error' });
			}
		});
		buildReducers(builder, createOrganisationProfile, 'createOrganisationProfileRequest', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.organisation.profile.createProfile.successMessage'), {
					variant: 'success'
				});
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.organisation.profile.createProfile.errorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, updateOrganisationProfile, 'updateOrganisationProfileRequest', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.organisation.profile.updateProfile.successMessage'), {
					variant: 'success'
				});
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.organisation.profile.updateProfile.errorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, readOrganisationProfile, 'readOrganisationProfileRequest', {
			onFulfilled: (state, payload) => {
				state.organisationProfileList = payload;
			}
		});
		buildReducers(builder, fetchCandidateInterestsMetrics, 'fetchCandidateInterestsMetrics', {
			onFulfilled: (state, payload) => {
				state.metrics.candidate_interests = payload;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, fetchSharedDocumentsRequestsMetrics, 'fetchSharedDocumentsRequestsMetrics', {
			onFulfilled: (state, payload) => {
				state.metrics.documents_requests = payload;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, listDocumentRequestNotificationCount, 'listDocumentRequestNotificationCount', {
			onFulfilled: (state, payload) => {
				state.shared_wallet_requests_notification_count = payload?.totalCount;
			}
		});
		buildReducers(builder, listCandidatInterestsNotifications, 'listCandidatInterestsNotifications', {
			onFulfilled: (state, payload) => {
				state.candidate_interests_notifications = payload?.totalCount;
			}
		});
		buildReducers(builder, listVerificationRequestNotifications, 'listVerificationRequestNotifications', {
			onFulfilled: (state, payload) => {
				state.verification_requests_notifications = payload?.totalCount;
			}
		});
		buildReducers(
			builder,
			fetchSharedDocumentsRequestsOutstandingDocumentMetrics,
			'fetchSharedDocumentsRequestsOutstandingDocumentMetrics',
			{
				onFulfilled: (state, payload) => {
					state.metrics.outstandings_document = payload;
				},
				onRejected(_, error) {
					enqueueSnackbar(error.message, { variant: 'error' });
				}
			}
		);
		buildReducers(
			builder,
			fetchCandidateInterestsRequestOutcomeMetrics,
			'fetchCandidateInterestsRequestOutcomeMetrics',
			{
				onFulfilled: (state, payload) => {
					state.metrics.outstandings_documents_request = payload;
				},
				onRejected(_, error) {
					enqueueSnackbar(error.message, { variant: 'error' });
				}
			}
		);
		buildReducers(builder, fetchCandidateInterestsLocationMetrics, 'fetchCandidateInterestsLocationMetrics', {
			onFulfilled: (state, payload) => {
				state.metrics.interests_location_metrics = payload;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(
			builder,
			fetchVerificationRequestsDocumentUploadMetrics,
			'fetchVerificationRequestsDocumentUploadMetrics',
			{
				onFulfilled: (state, payload) => {
					state.metrics.verifications_requests_document_upload = payload;
				},
				onRejected(_, error) {
					enqueueSnackbar(error.message, { variant: 'error' });
				}
			}
		);
		buildReducers(builder, fetchVerificationRequestsMetrics, 'fetchVerificationRequestsMetrics', {
			onFulfilled: (state, payload) => {
				state.metrics.verification_requests_metrics = payload;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, fetchCandidateInterestsCompanyMetrics, 'fetchCandidateInterestsCompanyMetrics', {
			onFulfilled: (state, payload) => {
				state.metrics.candidate_interests_company_metrics = payload;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, listBulkUploadMetrics, 'listBulkUploadMetrics', {
			onFulfilled: (state, payload) => {
				const list = state.bulk_upload_metrics;
				list.items = payload.items;
				list.next = payload.next;
				list.totalCount = payload.totalCount;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});

		buildReducers(builder, listOrganisationUploadedDocuments, 'listOrganisationUploadedDocuments', {
			onFulfilled: (state, payload) => {
				const list = state.uploaded_documents;
				list.items = payload.items;
				list.next = payload.next;
				list.totalCount = payload.totalCount;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, listOrganisationFailedSends, 'listOrganisationFailedSends', {
			onFulfilled: (state, payload) => {
				state.uploaded_documents_failed = payload.items;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, listOrganisationSendHistory, 'listOrganisationSendHistory', {
			onFulfilled: (state, payload) => {
				const list = state.send_history;
				list.items = payload.items;
				list.next = payload.next;
				list.totalCount = payload.totalCount;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, cancelUploadedDocument, 'cancelUploadedDocument', {
			onFulfilled: (state, payload) => {
				// Reflected locally rather than refetching: the row stays put with its new
				// status, so the reader can see what they just did instead of watching it
				// vanish out of a reordered list.
				const row = state.uploaded_documents.items.find((item: SentDocument) => item.id === payload.documentId);
				if (row) {
					row.bulkUploadStatus = 'cancelled';
				}
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, replaceUploadedDocument, 'replaceUploadedDocument', {
			onFulfilled: (state, payload) => {
				const row = state.uploaded_documents.items.find((item: SentDocument) => item.id === payload.documentId);
				if (row && payload.verificationRevoked) {
					// Replace always revokes: an attestation must never stand over bytes it
					// did not sign. Reflected here so the chip does not keep claiming a tick
					// the document no longer has.
					row.verified = false;
					row.verification = undefined;
				}
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, setUploadedDocumentVerification, 'setUploadedDocumentVerification', {
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, setUploadedDocumentAccess, 'setUploadedDocumentAccess', {
			// Patched in place: unlike verification, the stored shape is exactly the two flags,
			// written only as `false`.
			onFulfilled: (state, payload) => {
				const row = state.uploaded_documents.items.find((item: SentDocument) => item.id === payload.documentId);
				if (row) {
					if (payload.viewable) delete row.viewable;
					else row.viewable = false;
					if (payload.shareable) delete row.shareable;
					else row.shareable = false;
				}
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, revokeUploadedDocumentVerification, 'revokeUploadedDocumentVerification', {
			onFulfilled: (state, payload) => {
				const row = state.uploaded_documents.items.find((item: SentDocument) => item.id === payload.documentId);
				if (row) {
					row.verified = false;
					row.verification = { status: 'cancelled' };
				}
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, retractSendBatch, 'retractSendBatch', {
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, listBulkUploadDocumentMetrics, 'listBulkUploadDocumentMetrics', {
			onFulfilled: (state, payload) => {
				const list = state.bulk_upload_document_metrics;
				list.items = payload.items;
				list.next = payload.next;
				list.totalCount = payload.totalCount;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, fetchPearsanaCandidateInterestsMetrics, 'fetchPearsanaCandidateInterestsMetrics', {
			onFulfilled: (state, payload) => {
				state.metrics.pearsana_candidate_interests_metrics = payload;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(
			builder,
			fetchPearsanaCandidateInterestsLocationMetrics,
			'fetchPearsanaCandidateInterestsLocationMetrics',
			{
				onFulfilled: (state, payload) => {
					state.metrics.pearsana_candidate_interests_location_metrics = payload;
				},
				onRejected(_, error) {
					enqueueSnackbar(error.message, { variant: 'error' });
				}
			}
		);
		buildReducers(
			builder,
			fetchVerificationRequestsResponseTimeMetrics,
			'fetchVerificationRequestsResponseTimeMetrics',
			{
				onFulfilled: (state, payload) => {
					state.metrics.verification_requests_response_time_metrics = payload;
				},
				onRejected(_, error) {
					enqueueSnackbar(error.message, { variant: 'error' });
				}
			}
		);
		buildReducers(
			builder,
			fetchVerificationRequestsAdditionalMetrics,
			'fetchVerificationRequestsAdditionalMetrics',
			{
				onFulfilled: (state, payload) => {
					state.metrics.verification_requests_additional_metrics = payload;
				},
				onRejected(_, error) {
					enqueueSnackbar(error.message, { variant: 'error' });
				}
			}
		);
		buildReducers(builder, createOrgAdvert, 'createOrgAdvertRequest', {
			onFulfilled: (state) => {
				const orgType = state.current.type;
				enqueueSnackbar(i18n.t(`pages.organisation.portal.${orgType}.create.successMessage`), {
					variant: 'success'
				});
			},
			onRejected: (state) => {
				const orgType = state.current.type;
				enqueueSnackbar(i18n.t(`pages.organisation.portal.${orgType}.create.errorMessage`), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, deleteOrgAdvert, 'deleteOrgAdvertRequest', {
			onFulfilled: (state) => {
				const orgType = state.current.type;
				enqueueSnackbar(i18n.t(`pages.organisation.portal.${orgType}.delete.successMessage`), {
					variant: 'success'
				});
			},
			onRejected: (state) => {
				const orgType = state.current.type;
				enqueueSnackbar(i18n.t(`pages.organisation.portal.${orgType}.delete.errorMessage`), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, updateOrgAdvert, 'updateOrgAdvertRequest', {
			onFulfilled: (state) => {
				const orgType = state.current.type;
				enqueueSnackbar(i18n.t(`pages.organisation.portal.${orgType}.update.successMessage`), {
					variant: 'success'
				});
			},
			onRejected: (state) => {
				const orgType = state.current.type;
				enqueueSnackbar(i18n.t(`pages.organisation.portal.${orgType}.update.errorMessage`), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, readOrgAdverts, 'readOrgAdverts', {
			onFulfilled: (state, payload) => {
				state.adverts = payload;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, readSingleOrgAdvert, 'readSingleOrgAdvert', {
			onFulfilled: (state, payload) => {
				state.singleAdvert = payload;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, fetchSpotlightCount, 'fetchSpotlightCountRequest', {
			onFulfilled: (state, payload) => {
				state.spotlightCount = payload;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, listStudents, 'readStudentListRequest', {
			onFulfilled: (state, payload) => {
				const list = state.studentList;
				list.items = payload.items;
				list.next = payload.next;
				list.totalCount = payload.totalCount;
			},
			onRejected(_, error) {
				enqueueSnackbar(error.message, { variant: 'error' });
			}
		});
		buildReducers(builder, listProspectiveIndividuals, 'prospectiveIndividualRequest', {
			onFulfilled: (state, payload) => {
				const prospectiveIndividuals = state.prospectiveIndividuals;
				prospectiveIndividuals.items = payload.items;
				prospectiveIndividuals.next = payload.next;
				prospectiveIndividuals.totalCount = payload.totalCount;
			}
		});
		buildReducers(builder, listPortalLogs, 'listPortalLogsRequest', {
			onFulfilled: (state, payload) => {
				const portaLogs = state.portaLogs;
				portaLogs.items = payload.items;
				portaLogs.next = payload.next;
				portaLogs.totalCount = payload.totalCount;
			}
		});
		buildReducers(builder, listDetailedPortalLogs, 'listDetailedPortalLogsRequest', {
			onFulfilled: (state, payload) => {
				const detailedPortaLogs = state.detailedPortaLogs;
				detailedPortaLogs.items = payload.items;
				detailedPortaLogs.next = payload.next;
				detailedPortaLogs.totalCount = payload.totalCount;
			}
		});
		buildReducers(builder, getBillingAdminOrders, 'billingAdminOrdersRequest', {
			onFulfilled: (state, payload) => {
				state.billingAdminOrders = payload;
			}
		});
		buildReducers(builder, getBillingAdminGrants, 'billingAdminGrantsRequest', {
			onFulfilled: (state, payload) => {
				state.billingAdminGrants = payload;
				state.isLoading = false;
			},
			onPending: (state) => {
				state.isLoading = true;
			},
			onRejected: (state) => {
				state.isLoading = false;
			}
		});
		buildReducers(builder, getActiveSubscriptions, 'organisationActiveSubscriptionsRequest', {
			onFulfilled: (state, payload) => {
				const { subscriptions, next, totalCount } = payload;
				state.organisationActiveSubscriptions.items = subscriptions.map((subscription: any) => ({
					...subscription,
					id: subscription.orderId
				}));
				state.organisationActiveSubscriptions.next = next;
				state.organisationActiveSubscriptions.totalCount = totalCount;
			}
		});
		buildReducers(builder, cancelActiveSubscription, 'cancelActiveSubscriptionRequest', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('common.types.user.cancelSubscriptionSuccessMessage'), {
					variant: 'success'
				});
			}
		});
		buildReducers(builder, listFamilyAdmins, 'listFamilyAdminsRequest', {
			onFulfilled: (state, payload) => {
				state.familyAdmins = payload;
			}
		});
		buildReducers(builder, listFamilyChildren, 'listFamilyChildrenRequest', {
			onFulfilled: (state, payload) => {
				state.familyChildren = payload;
			}
		});
		buildReducers(builder, listFamilyMemberNotifications, 'listFamilyMemberNotificationsRequest', {
			onFulfilled: (state, payload) => {
				const counts: { [userId: string]: number } = {};
				payload?.members?.forEach((m: { userId: string; notificationCount: number }) => {
					counts[m.userId] = m.notificationCount;
				});
				state.family_member_notifications = counts;
			}
		});
		buildReducers(builder, dismissFamilyMemberNotifications, 'dismissFamilyMemberNotificationsRequest', {
			onFulfilled: (state, payload) => {
				const { userId } = payload;
				delete state.family_member_notifications[userId];
			}
		});
		buildReducers(builder, readVerifyingOrganisation, 'readVerifyingOrganisationRequest', {
			onFulfilled: (state, payload) => {
				state.verifyingOrganisation = payload;
			}
		});

		// ── App Clients ─────────────────────────────────────────────────────
		buildReducers(builder, listAppClients, 'appClientReadStatus', {
			onFulfilled: (state, payload) => {
				state.appClients = payload.items ?? [];
			},
			onRejected: (_, error) => {
				enqueueSnackbar(error.message || i18n.t('pages.organisation.developer.appClients.loadError'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, createAppClient, 'createAppClientRequest', {
			onFulfilled: (state, payload) => {
				// Append the new client (without clientSecret) to the list
				const { clientSecret: _clientSecret, ...client } = payload;
				state.appClients.push(client);
				enqueueSnackbar(i18n.t('pages.organisation.developer.appClients.createSuccess'), {
					variant: 'success'
				});
			},
			onRejected: (_, error) => {
				enqueueSnackbar(error.message || i18n.t('pages.organisation.developer.appClients.createError'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, deleteAppClient, 'deleteAppClientRequest', {
			onFulfilled: (state, payload) => {
				state.appClients = state.appClients.filter((c: AppClient) => c.clientId !== payload.clientId);
				enqueueSnackbar(i18n.t('pages.organisation.developer.appClients.deleteSuccess'), {
					variant: 'success'
				});
			},
			onRejected: (_, error) => {
				enqueueSnackbar(error.message || i18n.t('pages.organisation.developer.appClients.deleteError'), {
					variant: 'error'
				});
			}
		});

		// ── Organisation Constituents ───────────────────────────────────────
		buildReducers(builder, getPartnerConfig, 'partnerConfigRequest', {
			onFulfilled: (state, payload) => {
				state.partnerConfig = payload.partnerConfig ?? null;
			}
		});
		buildReducers(builder, savePartnerConfig, 'savePartnerConfigRequest', {
			onFulfilled: (state, payload) => {
				state.partnerConfig = payload.partnerConfig;
				enqueueSnackbar(i18n.t('pages.organisation.partnerConfig.saveSuccess'), { variant: 'success' });
			},
			onRejected: (_, error) => {
				enqueueSnackbar(error.message || i18n.t('pages.organisation.partnerConfig.saveError'), {
					variant: 'error'
				});
			}
		});
		builder.addCase(fetchConstituentCount.fulfilled, (state, action) => {
			if (action.payload.organisationId !== state.constituentsScope.organisationId) {
				return;
			}
			state.constituentsTotal = action.payload.total;
		});
		buildReducers(builder, listConstituents, 'listConstituentsRequest', {
			onFulfilled: (state, payload) => {
				// Drop anything the view has already moved past: a response for the
				// previous organisation would otherwise show its constituents here, and a
				// superseded search would overwrite the results of the current one.
				const scope = state.constituentsScope;
				if (payload.organisationId !== scope.organisationId || (payload.search ?? '') !== scope.search) {
					return;
				}

				const incoming = payload.items ?? [];
				state.constituents = payload.append ? [...state.constituents, ...incoming] : incoming;
				state.constituentsNextToken = payload.nextToken ?? null;
				state.constituentsTruncated = Boolean(payload.truncated);
			},
			onRejected: (_, error) => {
				enqueueSnackbar(error.message || i18n.t('pages.organisation.constituents.loadError'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, createConstituentImport, 'createConstituentImportRequest', {
			onRejected: (_, error) => {
				enqueueSnackbar(error.message || i18n.t('pages.organisation.constituents.upload.createError'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, uploadConstituentFile, 'uploadConstituentFileRequest', {
			onRejected: (_, error) => {
				enqueueSnackbar(error.message || i18n.t('pages.organisation.constituents.upload.transferError'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, startConstituentImport, 'startConstituentImportRequest', {
			onFulfilled: (state, payload) => {
				state.constituentImport = payload;
			},
			onRejected: (_, error) => {
				enqueueSnackbar(error.message || i18n.t('pages.organisation.constituents.upload.startError'), {
					variant: 'error'
				});
			}
		});
		// Polling deliberately has no request-status wiring and no snackbar: it fires
		// every 5s, and a transient failure must not spam the admin or flip the
		// dialog into an error state the next tick would clear.
		builder.addCase(pollConstituentImport.fulfilled, (state, action) => {
			const job = action.payload;
			state.constituentImport = job;

			// A COMPLETED purge is proof the roster is empty: every chunk reported in,
			// and the organisation lock stops anything adding rows while it ran.
			//
			// Acting on that proof rather than re-fetching is deliberate. `orgIndex` is
			// a GSI and therefore eventually consistent, so a read issued milliseconds
			// after the last delete faithfully returns rows that no longer exist — which
			// is exactly why the table stayed populated until a manual refresh. Asking a
			// stale index cannot fix a stale index.
			// Scoped to the organisation whose roster is actually loaded. The slice holds
			// one job globally, so without this a purge completing for organisation A —
			// its poll still in flight when the admin navigated to B — would empty B's
			// roster and report B as having zero constituents.
			const sameOrganisation =
				!state.constituentsScope.organisationId ||
				job.organisationId === state.constituentsScope.organisationId;

			if (sameOrganisation && job.kind === 'purge' && job.status === 'completed') {
				state.constituents = [];
				state.constituentsNextToken = null;
				state.constituentsTotal = 0;
			}
		});
		buildReducers(builder, listConstituentImports, 'listConstituentImportsRequest', {
			onFulfilled: (state, payload) => {
				const items = payload.items ?? [];
				state.constituentImports = items;

				// Re-attach to an import or purge that is still running. The dialog tells
				// admins they may close it and pick the job up again, but nothing restored
				// it — so after a refresh the job existed server-side with no client
				// watching, and the controls unlocked while it was still going.
				if (!state.constituentImport) {
					const active = items.find((job) => CONSTITUENT_IMPORT_ACTIVE_STATUSES.includes(job.status));
					if (active) {
						state.constituentImport = active;
					}
				}
			}
		});
		buildReducers(builder, deleteConstituent, 'deleteConstituentRequest', {
			onFulfilled: (state, payload) => {
				state.constituents = state.constituents.filter(
					(c: OrganisationConstituent) => c.constituentId !== payload.constituentId
				);
				// The header and the purge confirmation both quote this, so leaving it
				// stale means "Remove all 10,000" over a roster of 9,999.
				if (typeof state.constituentsTotal === 'number' && state.constituentsTotal > 0) {
					state.constituentsTotal -= 1;
				}
				enqueueSnackbar(i18n.t('pages.organisation.constituents.deleteSuccess'), { variant: 'success' });
			},
			onRejected: (_, error) => {
				enqueueSnackbar(error.message || i18n.t('pages.organisation.constituents.deleteError'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, purgeConstituents, 'purgeConstituentsRequest', {
			onFulfilled: (state, payload) => {
				// The 202 only means the job started. Clearing the table here would
				// claim the roster was gone while the deletes were still running, so
				// the dashboard watches the job and reloads when it finishes.
				state.constituentImport = payload;
				enqueueSnackbar(i18n.t('pages.organisation.constituents.purgeStarted'), { variant: 'info' });
			},
			onRejected: (_, error) => {
				enqueueSnackbar(error.message || i18n.t('pages.organisation.constituents.purgeError'), {
					variant: 'error'
				});
			}
		});

		// ── Partner API Logs ────────────────────────────────────────────────
		buildReducers(builder, fetchPartnerApiLogs, 'partnerApiLogsRequest', {
			onFulfilled: (state, payload) => {
				const newLogs = payload.logs ?? [];
				state.partnerApiLogs = {
					logs: payload.append ? [...state.partnerApiLogs.logs, ...newLogs] : newLogs,
					nextToken: payload.nextToken ?? null
				};
			},
			onRejected: (_, error) => {
				enqueueSnackbar(error.message || i18n.t('pages.organisation.developer.apiLogs.loadError'), {
					variant: 'error'
				});
			}
		});
	}
});

export const { resetOrganisation, addNewOrgToList, setConstituentScope } = organisationSlice.actions;
export default organisationSlice.reducer;
